import React from "react";
import { Box } from "@mui/material";
import {
  certificatePageSize,
  certificatePaperColour,
  type CertificateOrientation,
} from "../../../domain/certificates/certificateTemplates";
import { CentredLayout, type CertificateDesign } from "./certificateParts";

/** The paper panel, this far in from the page edge, over the watercolour. */
const PANEL_INSET = 44;
/** The white dots on the watercolour, this far apart. */
const DOT_SPACING = 30;

const BEE_INK = "#222222";
const WING = "#d8ecfb";
const WING_EDGE = "#7fb3dc";

/** Where the two bees sit, and how big: beside the name on landscape, in the top corners on portrait. */
function beesFor(
  orientation: CertificateOrientation,
  width: number,
  height: number,
): { y: number; scale: number; fromSide: number } {
  return orientation === "landscape"
    ? { y: height * 0.54, scale: 1, fromSide: 142 }
    : { y: 168, scale: 0.85, fromSide: 128 };
}

/**
 * The part of an ellipse centred on the origin between x1 and x2: one of a
 * bee's stripes.
 */
function ellipseBand(rx: number, ry: number, x1: number, x2: number): string {
  const y = (x: number) => ry * Math.sqrt(1 - (x / rx) ** 2);
  return `M${x1},${-y(x1)} A${rx},${ry} 0 0 1 ${x2},${-y(x2)} L${x2},${y(x2)} A${rx},${ry} 0 0 1 ${x1},${y(x1)} Z`;
}

/**
 * A smiling bee in a graduation cap, facing right, centred on the origin.
 * About 130 wide.
 */
const Bee: React.FC<{ transform: string; body: string }> = ({
  transform,
  body,
}) => (
  <g transform={transform}>
    {/* Wings, behind the body. */}
    <ellipse
      cx={-14}
      cy={-34}
      rx={18}
      ry={28}
      transform="rotate(-24 -14 -34)"
      fill={WING}
      stroke={WING_EDGE}
      strokeWidth={2.5}
    />
    <ellipse
      cx={8}
      cy={-36}
      rx={16}
      ry={25}
      transform="rotate(18 8 -36)"
      fill={WING}
      stroke={WING_EDGE}
      strokeWidth={2.5}
    />
    {/* Stinger. */}
    <polygon points="-44,-4 -60,2 -44,8" fill={BEE_INK} />
    {/* Body and stripes. */}
    <ellipse rx={44} ry={30} fill={body} stroke={BEE_INK} strokeWidth={3} />
    <path d={ellipseBand(44, 30, -30, -18)} fill={BEE_INK} />
    <path d={ellipseBand(44, 30, -6, 6)} fill={BEE_INK} />
    {/* Head, with a smile. */}
    <circle cx={46} cy={-8} r={24} fill={body} stroke={BEE_INK} strokeWidth={3} />
    <circle cx={40} cy={-12} r={3} fill={BEE_INK} />
    <circle cx={55} cy={-12} r={3} fill={BEE_INK} />
    <path
      d="M38,-1 Q47,8 57,-1"
      fill="none"
      stroke={BEE_INK}
      strokeWidth={2.5}
      strokeLinecap="round"
    />
    <circle cx={34} cy={-3} r={4} fill="#f4a6a6" opacity={0.8} />
    {/* Graduation cap: the skull cap, the board and a tassel. */}
    <path d="M32,-28 Q46,-40 60,-28 L60,-34 L32,-34 Z" fill={BEE_INK} />
    <polygon points="46,-48 74,-38 46,-28 18,-38" fill={BEE_INK} />
    <path
      d="M46,-38 L66,-34 L66,-20"
      fill="none"
      stroke={body}
      strokeWidth={2.5}
      strokeLinecap="round"
    />
    <circle cx={66} cy={-18} r={3.5} fill={body} />
  </g>
);

/** A puffy cloud, centred on the origin, about 170 wide. */
const Cloud: React.FC<{ transform: string }> = ({ transform }) => (
  <g transform={transform} fill="#ffffff">
    <circle cx={-52} cy={10} r={30} />
    <circle cx={-16} cy={-14} r={40} />
    <circle cx={28} cy={-6} r={36} />
    <circle cx={60} cy={14} r={26} />
    <rect x={-80} y={10} width={164} height={30} rx={15} />
  </g>
);

/** One tail of the ribbon, notched at its outer end. */
const RIBBON_TAIL = "0,0 60,0 60,100 0,100 18,50";

/** The ribbon the title sits on, with a notched tail behind each end. */
const Ribbon: React.FC<{ colour: string; children: React.ReactNode }> = ({
  colour,
  children,
}) => (
  <Box
    sx={{
      position: "relative",
      zIndex: 0,
      bgcolor: colour,
      px: "36px",
      pt: "4px",
      mb: "16px",
      boxShadow: "0 3px 0 rgba(0, 0, 0, 0.12)",
      // The title's own space below it, inside the ribbon.
      "& > *": { mb: "4px" },
    }}
  >
    {[-1, 1].map((side) => (
      <Box
        key={side}
        component="svg"
        viewBox="0 0 60 100"
        preserveAspectRatio="none"
        aria-hidden="true"
        sx={{
          position: "absolute",
          zIndex: -1,
          top: "16px",
          height: "100%",
          width: 60,
          [side < 0 ? "left" : "right"]: -44,
          transform: side < 0 ? "none" : "scaleX(-1)",
        }}
      >
        <polygon points={RIBBON_TAIL} fill={colour} />
        <polygon points={RIBBON_TAIL} fill="#000000" opacity={0.14} />
      </Box>
    ))}
    {children}
  </Box>
);

/**
 * Bumblebee: a white panel on a watercolour wash with white dots and clouds,
 * the title on a ribbon, and a bee in a graduation cap on either side, like a
 * Grade R graduation certificate.
 */
export const bumblebeeDesign: CertificateDesign = {
  bodyFont: "nunito",
  Layout: ({ template, text, orientation }) => {
    const { primaryColour: primary, accentColour: accent } = template;
    const { width, height } = certificatePageSize(orientation);
    const bees = beesFor(orientation, width, height);
    const dots = [];
    for (let y = DOT_SPACING / 2; y < height; y += DOT_SPACING) {
      for (let x = DOT_SPACING / 2; x < width; x += DOT_SPACING) {
        // Every other row is shifted half a step, like polka dots.
        const shift = (Math.round(y / DOT_SPACING) % 2) * (DOT_SPACING / 2);
        dots.push({ x: x + shift, y });
      }
    }
    return (
      <CentredLayout
        template={template}
        text={text}
        orientation={orientation}
        titleWrapper={(title) => <Ribbon colour={accent}>{title}</Ribbon>}
        lettering={{ titleColour: "#1a1a1a" }}
        inset={{
          // Between the bees on landscape, below them on portrait.
          landscape: "72px 230px 64px",
          portrait: "250px 104px 76px",
        }}
        frame={
          <Box
            component="svg"
            viewBox={`0 0 ${width} ${height}`}
            aria-hidden="true"
            sx={{ position: "absolute", inset: 0, width, height }}
          >
            {/* The watercolour: the primary colour, with lighter and darker washes. */}
            <rect width={width} height={height} fill={primary} />
            <ellipse cx={width * 0.15} cy={height * 0.2} rx={width * 0.3} ry={height * 0.25} fill="#ffffff" opacity={0.22} />
            <ellipse cx={width * 0.85} cy={height * 0.75} rx={width * 0.32} ry={height * 0.3} fill="#ffffff" opacity={0.18} />
            <ellipse cx={width * 0.75} cy={height * 0.15} rx={width * 0.2} ry={height * 0.18} fill="#000000" opacity={0.1} />
            <ellipse cx={width * 0.2} cy={height * 0.85} rx={width * 0.25} ry={height * 0.2} fill="#000000" opacity={0.12} />
            {dots.map(({ x, y }) => (
              <circle key={`${x},${y}`} cx={x} cy={y} r={3.5} fill="#ffffff" opacity={0.75} />
            ))}
            <rect
              x={PANEL_INSET}
              y={PANEL_INSET}
              width={width - PANEL_INSET * 2}
              height={height - PANEL_INSET * 2}
              rx={28}
              fill={certificatePaperColour(template)}
            />
            <Cloud transform={`translate(${PANEL_INSET + 50} ${PANEL_INSET + 30}) scale(0.9)`} />
            <Cloud transform={`translate(${width - PANEL_INSET - 50} ${PANEL_INSET + 30}) scale(-0.9 0.9)`} />
            <Bee
              transform={`translate(${bees.fromSide} ${bees.y}) scale(${bees.scale})`}
              body={accent}
            />
            <Bee
              transform={`translate(${width - bees.fromSide} ${bees.y}) scale(${-bees.scale} ${bees.scale})`}
              body={accent}
            />
          </Box>
        }
      />
    );
  },
};
